import { memo, useMemo, useEffect, useState } from 'react'
import { Select, Button, MenuIcon, TruncateText } from '@panwds/react-ui'
import { actions, getTsgAccess, history, store } from '@sparky/framework'
import {
  MANAGE_LICENSE,
  MANAGE_SUBSCRIPTIONS,
  MANAGE_USER_AND_ROLE,
  MANAGE_DEVICE_ASSOCIATIONS,
  MANAGE_IP_ALLOW_LIST,
  MANAGE_PRODUCTS
} from '../../../common/constants/LabelConstants'
import {
  VIEW_MANAGE_IAM,
  VIEW_MANAGE_SUBSCRIPTION,
  VIEW_MANAGE_TENANT,
  VIEW_DEVICE_ASSOCIATIONS,
  VIEW_IP_ALLOW_LIST,
  SUBSCRIPTION_PERMISSION_SET,
  TENANCY_PERMISSION_SET,
  IAM_PERMISSION_SET,
  DEVICES_PERMISSION_SET,
  IP_ALLOW_PERMISSION_SET
} from '../../../common/constants/GlobalConstants'
import HubLogo from './HubLogo'
import { CommonServicesIcon } from './CommonServicesIcon'
import HubToggle, { prefetchToggleHub } from './HubToggle'
import UserIcon from './UserIcon'
import { HUB_PATH } from '../../constants'
import TenantSwitcher, { useShouldRender } from './TenantSwitcher'
import {
  FF_DAS_ENABLED,
  FF_COMMON_SERVICES_CUSTOM_ROLES,
  FF_IP_ALLOW_LIST,
  canUseIpAllow,
  FF_DEFAULT_TSG
} from '../../../common/utils/FeatureFlags'
import { getFrameworkVar, getVarValue } from '../../../common/utils/Sparky'
import './TopBar.scss'
import type { Changes } from '@panwds/react-ui'
import type { VFC } from 'react'
import type { UserAndTsg, ReadonlyTsgTree } from '../../types'
import ElaDropDown from './ElaDropDown'
import type { AppManifests } from '@sparky/framework'

// const openTenantSwitcher = () => {
//   actions.openCommonModule(VIEW_TENANT_SWITCHER)
// }

const openCommonModule = ({ selectedItem }: Changes) => {
  actions.openCommonModule(selectedItem.value as string, store.stateRef.current.auth?.tsg_id)
}

const goHome = () => {
  if (window.location.pathname !== HUB_PATH) {
    history.push(HUB_PATH)
  }
}

const dasEnabled = Boolean(getVarValue(FF_DAS_ENABLED)) && getFrameworkVar(FF_DAS_ENABLED) !== false
const customRolesEnabled = getVarValue(FF_COMMON_SERVICES_CUSTOM_ROLES, true)
const ipAllowEnabled = getVarValue(FF_IP_ALLOW_LIST, false)
const enableDefaultTSG = getVarValue(FF_DEFAULT_TSG)

const CommonServicesDropdown: VFC<{ disabled: boolean, tsg: any }> = ({ disabled, tsg }) => {

  const [subscriptionMenuVisible, setSubscriptionMenuVisible] = useState(false)
  const [tenantManagementMenuVisible, setTenantManagementMenuVisible] = useState(false)
  const [iamMenuVisible, setIamMenuVisible] = useState(false)
  const [deviceMenuVisible, setDeviceMenuVisible] = useState(false)
  const [ipAllowVisible, setIpAllowVisible] = useState(false)

  const isSingleTenant = enableDefaultTSG && !store.stateRef.current.auth?.tsgs?.hasMsp

  useEffect(() => {
    let mounted = true
    const fetchMenuVisible = async () => {
      if (tsg.id) {
        const visibleObj = await getTsgAccess(tsg.id).then((tsgAccess: any) => {
          return {
            subscription: tsgAccess.hasPermissionSetChildren(SUBSCRIPTION_PERMISSION_SET) || !customRolesEnabled,
            tenancy: tsgAccess.hasPermissionSetChildren(TENANCY_PERMISSION_SET) || !customRolesEnabled,
            iam: tsgAccess.hasPermissionSetChildren(IAM_PERMISSION_SET) || !customRolesEnabled,
            das: tsgAccess.hasPermissionSetChildren(DEVICES_PERMISSION_SET) || !customRolesEnabled,
            ipAllow: (canUseIpAllow(tsg) && tsgAccess.hasPermissionSetChildren(IP_ALLOW_PERMISSION_SET)) || !customRolesEnabled
          }
        })
        if (mounted) {
          setSubscriptionMenuVisible(visibleObj.subscription)
          setTenantManagementMenuVisible(visibleObj.tenancy)
          setIamMenuVisible(visibleObj.iam)
          setDeviceMenuVisible(visibleObj.das)
          setIpAllowVisible(visibleObj.ipAllow)
        }
      }
      else {
        if (mounted) {
          setSubscriptionMenuVisible(false)
          setTenantManagementMenuVisible(false)
          setIamMenuVisible(false)
          setDeviceMenuVisible(false)
          setIpAllowVisible(false)
        }
      }
    }
    fetchMenuVisible()
    return () => {
      mounted = false
    }
  }, [tsg, tsg.id])

  const btn = (
    <div className='tw-dark'>
      <Button
        appearance='tertiary-clear'
        isMenu
        fullWidth={false}
        icon={<CommonServicesIcon className='cm-mr-2' />}
        size='md'
        addClassName='sparky-hub-services-btn cm-pl-4 cm-min-w-[11rem]'
        disabled={disabled}
      >Common Services</Button>
    </div>
  )
  if (disabled) {
    return btn
  }
  const menu = []
  if (subscriptionMenuVisible) {
    menu.push({ value: VIEW_MANAGE_SUBSCRIPTION, children: MANAGE_SUBSCRIPTIONS })
  }
  if (tenantManagementMenuVisible) {
    menu.push({ value: VIEW_MANAGE_TENANT, children: isSingleTenant ? MANAGE_PRODUCTS : MANAGE_LICENSE })
  }
  if (iamMenuVisible) {
    menu.push({ value: VIEW_MANAGE_IAM, children: MANAGE_USER_AND_ROLE })
  }
  if (dasEnabled && deviceMenuVisible) {
    menu.push({ value: VIEW_DEVICE_ASSOCIATIONS, children: MANAGE_DEVICE_ASSOCIATIONS })
  }
  if (ipAllowEnabled && ipAllowVisible) {
    menu.push({ value: VIEW_IP_ALLOW_LIST, children: MANAGE_IP_ALLOW_LIST })
  }
  return <Select
    button={btn}
    items={menu}
    // fitToItemWidth
    onChange={openCommonModule}
    selectedItem={[]}
    menuStyle={{ minWidth: '11rem' }}
  />
}

const UserButton: VFC<UserAndTsg & { isOpen: boolean, open?: () => void }> = ({ user, tsg, open, isOpen }) => {
  const userName: string = useMemo(() => {
    if (!user) {
      return ''
    }
    const { firstname, lastname, email } = user
    return `${firstname} ${lastname}`.trim() || email
  }, [user])

  return <div className='tw-dark'>
    <Button
      appearance='tertiary-clear'
      onClick={open}
      fullWidth={false}
      icon={<UserIcon className='tw-absolute' />}
      addClassName='sparky-hub-user-profile'
      dataTestId='sparky-hub-user-profile'
    >
      <div className='sparky-hub-user-btn-content' data-email={user.email || ''} data-tsg-id={tsg?.id || ''}>
        <TruncateText>{userName}</TruncateText>
        <div className='sparky-hub-user-btn-caret'><MenuIcon isOpen={isOpen} /></div>
        {tsg && <TruncateText addClassName='sparky-hub-user-btn-tsg'>{tsg.display_name} <small className='cm-opacity-50'>- {tsg.id}</small></TruncateText>}
      </div>
    </Button>
  </div>
}

type UserTsgs = UserAndTsg & { tsgs: ReadonlyTsgTree }
type UserTsgsAndApps = UserTsgs & { apps: AppManifests }

const UserDropdown: VFC<UserTsgs> = ({ user, tsg, tsgs }) => {
  const [isOpen, setOpen] = useState<boolean>(false)
  const [open, close] = useMemo(() => [
    setOpen.bind(undefined, true),
    setOpen.bind(undefined, false),
  ], [])
  const canOpenAgain = useShouldRender(isOpen)
  return <>
    <UserButton user={user} tsg={tsg} isOpen={isOpen} open={canOpenAgain ? undefined : open} />
    <TenantSwitcher tsg={tsg} tsgs={tsgs} isOpen={isOpen} setOpen={setOpen} close={close} />
  </>
}

const TopBarComp: VFC<UserTsgsAndApps> = ({ user, tsg, tsgs, apps }) => {
  useEffect(prefetchToggleHub, [])
  return (
    <div className='sparky-hub-topbar' data-testid='sparky-hub-topbar'>
      <div className='sparky-hub-logo' onClick={goHome}><HubLogo className='sparky-hub-logo-svg' /></div>
      <HubToggle user={user} />
      <ElaDropDown user={user} tsg={tsg} tsgs={tsgs} apps={apps as AppManifests} />
      <div className='sparky-hub-topbar-spacer'></div>
      <div className='sparky-hub-actions'>
        <CommonServicesDropdown disabled={!tsg || tsg.isNone} tsg={tsg} />
        <UserDropdown user={user} tsg={tsg} tsgs={tsgs} />
        {/* <button className='sparky-hub-apps-btn' onClick={openTenantSwitcher}><AppSwitcherIcon size='md' className='cm-block' /></button> */}
      </div>
    </div>
  )
}

const TopBarPlaceholderComp: VFC<{}> = () => (
  <div className='sparky-hub-topbar' data-testid='sparky-hub-topbar-placeholder'>
    <div className='sparky-hub-logo' onClick={goHome}><HubLogo className='sparky-hub-logo-svg' /></div>
    <div className='sparky-hub-topbar-spacer'></div>
    <div className='sparky-hub-actions'>
      {/* <button className='sparky-hub-apps-btn'><AppSwitcherIcon size='md' className='cm-block' /></button> */}
    </div>
  </div>
)

export const TopBar = memo(TopBarComp)
export const TopBarPlaceholder = memo(TopBarPlaceholderComp)
export default TopBar
